import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

interface OverlayShellProps {
	label: string;
	onClose: () => void;
	className?: string;
	backdrop?: boolean;
	children: ReactNode;
}

const FOCUSABLE =
	':is(a[href],button,textarea,input,select,[tabindex]):not(:disabled):not([tabindex="-1"]):not([hidden]):not([type="hidden"])';

export function OverlayShell({
	label,
	onClose,
	className,
	backdrop = true,
	children,
}: OverlayShellProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [trigger] = useState(() => document.activeElement as HTMLElement | null);

	useEffect(() => {
		const container = containerRef.current;

		if (container && !container.contains(document.activeElement)) {
			const focusable = container.querySelector<HTMLElement>(FOCUSABLE);
			(focusable ?? container).focus();
		}

		return () => {
			trigger?.focus();
		};
	}, [trigger]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			const container = containerRef.current;

			if (container === null) {
				return;
			}

			const overlays = document.body.querySelectorAll<HTMLElement>(
				'[data-component="overlay-shell"]',
			);

			if (overlays.item(overlays.length - 1) !== container) {
				return;
			}

			if (event.key === "Escape") {
				onClose();

				return;
			}

			if (event.key !== "Tab") {
				return;
			}

			const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
				(element) =>
					element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden",
			);

			if (focusable.length === 0) {
				event.preventDefault();
				container.focus();

				return;
			}

			const first = focusable[0];
			const last = focusable.at(-1);
			const active = document.activeElement;
			const focusOutside = !focusable.some((element) => element === active);

			if (event.shiftKey && (active === first || focusOutside)) {
				event.preventDefault();
				last?.focus();

				return;
			}

			if (!event.shiftKey && (active === last || focusOutside)) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<div
			ref={containerRef}
			data-component="overlay-shell"
			role="dialog"
			aria-modal="true"
			aria-label={label}
			tabIndex={-1}
			className={cn("fixed inset-0 z-50 flex outline-none", className)}
		>
			{backdrop && (
				<button
					type="button"
					tabIndex={-1}
					aria-label="Fechar"
					onClick={onClose}
					className="absolute inset-0 -z-10 bg-foreground/30 backdrop-blur-[1px]"
				/>
			)}

			{children}
		</div>,
		document.body,
	);
}
